<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useOrganizationStore } from '@/stores/organizationStore'
import { rawApi } from '@/services/apiClient'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orgStore = useOrganizationStore()

// OAuth parameters from the query string
const clientId = route.query.client_id
const redirectURI = route.query.redirect_uri
const requestedScopes = (route.query.scope || '').split(' ').filter(Boolean)

// Page state
const isLoading = ref(true)
const error = ref('')
const appDetails = ref(null)
const isAuthorizing = ref(false)

// The user selects which org to grant access to
const selectedOrgId = ref('')

const scopeGroups = [
  { label: 'Variables', prefix: 'variable', actions: ['create', 'read', 'update', 'delete', 'list'] },
  { label: 'Providers', prefix: 'provider', actions: ['create', 'read', 'update', 'delete', 'list'] },
  { label: 'Servers', prefix: 'server', actions: ['create', 'read', 'update', 'delete', 'list', 'create_token'] }
]

// All available scopes (for default selection)
const allScopes = scopeGroups.flatMap(g => g.actions.map(a => `${g.prefix}:${a}`))

// The user selects which scopes to grant — default to all
const selectedScopes = ref([...allScopes])

const toggleScope = (scope) => {
  if (selectedScopes.value.includes(scope)) {
    selectedScopes.value = selectedScopes.value.filter(s => s !== scope)
  } else {
    selectedScopes.value.push(scope)
  }
}

const userOrganizations = computed(() => authStore.organizations || [])
const singleOrg = computed(() => userOrganizations.value.length === 1 ? userOrganizations.value[0] : null)
const selectedOrg = computed(() => userOrganizations.value.find(o => o.id === selectedOrgId.value) || null)

onMounted(async () => {
  if (!clientId || !redirectURI) {
    error.value = 'Missing required parameters: client_id and redirect_uri are required.'
    isLoading.value = false
    return
  }

  if (!authStore.isAuthenticated) {
    await authStore.fetchUser()
    if (!authStore.isAuthenticated) {
      router.push(`/signin?redirect=${encodeURIComponent(route.fullPath)}`)
      return
    }
  }

  // Ensure organizations are loaded
  if (!authStore.organizations || authStore.organizations.length === 0) {
    await orgStore.fetchOrganizations()
  }

  // Auto-select: always pre-select the first org (hidden if single)
  if (userOrganizations.value.length > 0) {
    selectedOrgId.value = userOrganizations.value[0].id
  }

  try {
    const res = await rawApi.get('/oauth2/authorize', {
      params: { client_id: clientId, redirect_uri: redirectURI }
    })
    appDetails.value = res.data
  } catch (err) {
    error.value = err.response?.data?.error?.message || 'Invalid client_id or redirect_uri.'
  } finally {
    isLoading.value = false
  }
})

const handleAuthorize = async () => {
  if (!selectedOrgId.value || selectedScopes.value.length === 0) return

  isAuthorizing.value = true
  error.value = ''
  try {
    const res = await rawApi.post('/oauth2/authorize', {
      client_id: clientId,
      redirect_uri: redirectURI,
      organization_id: selectedOrgId.value,
      scopes: selectedScopes.value,
    })

    if (res.data.redirectURIWithCode) {
      let finalUrl = res.data.redirectURIWithCode
      if (route.query.state) {
        // Appending state. Assuming redirectURIWithCode already has ?code=...
        const connector = finalUrl.includes('?') ? '&' : '?'
        finalUrl += `${connector}state=${encodeURIComponent(route.query.state)}`
      }
      window.location.href = finalUrl
    } else {
      throw new Error('No redirect URI received from server')
    }
  } catch (err) {
    error.value = err.response?.data?.error?.message || err.message || 'Authorization failed.'
  } finally {
    isAuthorizing.value = false
  }
}

const handleDeny = () => {
  let url = `${redirectURI}?error=access_denied`
  if (route.query.state) {
    url += `&state=${encodeURIComponent(route.query.state)}`
  }
  window.location.href = url
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-2xl border border-gray-200 shadow-lg w-full max-w-md">

      <!-- Loading -->
      <div v-if="isLoading" class="p-10 flex flex-col items-center gap-3 text-gray-400">
        <svg class="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-sm">Validating request...</p>
      </div>

      <!-- Error (no app details loaded) -->
      <div v-else-if="error && !appDetails" class="p-10 text-center">
        <div class="mb-4 flex justify-center">
          <div class="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
            <svg class="h-7 w-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.398 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
        <h2 class="text-lg font-semibold text-gray-800 mb-2">Authorization Error</h2>
        <p class="text-sm text-red-600">{{ error }}</p>
      </div>

      <!-- Consent UI -->
      <div v-else-if="appDetails">

        <!-- Header -->
        <div class="p-6 border-b border-gray-100 text-center">
          <div class="inline-flex items-center justify-center w-14 h-14 bg-black rounded-2xl mb-4">
            <svg class="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 class="text-xl font-bold text-gray-900">
            <span class="text-black">{{ appDetails.application?.name }}</span> wants access
          </h1>
          <p v-if="appDetails.application?.description" class="text-sm text-gray-500 mt-1">
            {{ appDetails.application.description }}
          </p>
          <p class="text-xs text-gray-400 mt-2">
            Registered by: <span class="font-medium text-gray-600">{{ appDetails.organization?.name }}</span>
          </p>
        </div>

        <div class="p-6 space-y-6">

          <!-- Organization: single org → compact pill, multiple → radio selector -->
          <div>
            <p class="text-sm font-bold text-gray-900 mb-2">Granting access to</p>

            <!-- Single org: auto-selected, just show a pill -->
            <div v-if="singleOrg" class="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <!-- hidden field so selectedOrgId is bound -->
              <input type="hidden" :value="singleOrg.id" />
              <div class="w-7 h-7 rounded-full bg-black flex items-center justify-center shrink-0">
                <span class="text-white text-xs font-bold">{{ singleOrg.name[0].toUpperCase() }}</span>
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ singleOrg.name }}</p>
                <p class="text-xs text-gray-400 font-mono">{{ singleOrg.id }}</p>
              </div>
              <svg class="ml-auto h-4 w-4 text-green-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>

            <!-- Multiple orgs: radio list -->
            <div v-else class="space-y-2">
              <label
                v-for="org in userOrganizations"
                :key="org.id"
                class="flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition"
                :class="selectedOrgId === org.id
                  ? 'border-black bg-gray-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300'"
              >
                <input type="radio" :value="org.id" v-model="selectedOrgId" class="accent-black shrink-0" />
                <div class="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0"
                  :class="selectedOrgId === org.id ? 'bg-black' : ''">
                  <span class="text-xs font-bold"
                    :class="selectedOrgId === org.id ? 'text-white' : 'text-gray-600'">
                    {{ org.name[0].toUpperCase() }}
                  </span>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ org.name }}</p>
                  <p class="text-xs text-gray-400 font-mono">{{ org.id }}</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Scope selector -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <p class="text-sm font-bold text-gray-900">Permissions requested</p>
              <span class="text-xs text-gray-400">{{ selectedScopes.length }} selected</span>
            </div>
            <p class="text-xs text-gray-500 mb-4">Review and adjust the permissions this application will have.</p>

            <div class="space-y-4">
              <div v-for="group in scopeGroups" :key="group.label">
                <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{{ group.label }}</p>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="action in group.actions"
                    :key="action"
                    type="button"
                    @click="toggleScope(`${group.prefix}:${action}`)"
                    class="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border font-medium transition-all"
                    :class="selectedScopes.includes(`${group.prefix}:${action}`)
                      ? 'bg-black text-white border-black shadow-sm'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-700'"
                  >
                    <svg v-if="selectedScopes.includes(`${group.prefix}:${action}`)"
                      class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    {{ action.replace('_', ' ') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Scope warning if none selected -->
            <p v-if="selectedScopes.length === 0" class="text-xs text-amber-600 mt-3 flex items-center gap-1.5">
              <svg class="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-8a1 1 0 112 0v3a1 1 0 11-2 0V5z" clip-rule="evenodd" />
              </svg>
              Select at least one permission to authorize.
            </p>
          </div>

          <!-- Error message (after load) -->
          <div v-if="error" class="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {{ error }}
          </div>
        </div>

        <!-- Action buttons -->
        <div class="px-6 pb-6 flex gap-3">
          <button
            @click="handleDeny"
            class="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition"
          >
            Deny
          </button>
          <button
            @click="handleAuthorize"
            :disabled="isAuthorizing || !selectedOrgId || selectedScopes.length === 0"
            class="flex-1 px-4 py-2.5 rounded-lg bg-black text-white text-sm font-semibold hover:bg-gray-800 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg v-if="isAuthorizing" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isAuthorizing ? 'Authorizing...' : 'Authorize' }}
          </button>
        </div>

        <p class="text-center text-xs text-gray-400 pb-5 px-6">
          By authorizing, you allow
          <span class="font-medium text-gray-600">{{ appDetails.application?.name }}</span>
          to access
          <span class="font-medium text-gray-600">{{ selectedOrg?.name || 'your organization' }}</span>
          with the chosen permissions.
        </p>
      </div>

    </div>
  </div>
</template>
